/**
 * Componente: Seleção de Equipamentos do Contrato
 * 
 * Este componente exibe e permite selecionar equipamentos de um contrato
 * com agrupamento hierárquico: Site > Modelo > Séries
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getContractEquipment, checkOpenOSByType } from '../services/equipmentService';

const EquipmentSelectionStep = ({
  contrato,
  selectedEquipments,
  setSelectedEquipments,
  onEquipmentsSelected,
  onConfirmEquipments // Callback para quando equipamentos são confirmados (após validação)
}) => {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSites, setExpandedSites] = useState(new Set());
  const [expandedModelos, setExpandedModelos] = useState(new Set());
  const [searchText, setSearchText] = useState('');
  const [showOsTypeDropdown, setShowOsTypeDropdown] = useState(false);
  const [showOsAbertasModal, setShowOsAbertasModal] = useState(false);
  const [osAbertas, setOsAbertas] = useState([]);
  const [isCheckingOs, setIsCheckingOs] = useState(false);
  const [selectedTipoOs, setSelectedTipoOs] = useState(null);
  const dropdownRef = useRef(null);

  // Carregar equipamentos do contrato
  useEffect(() => {
    if (!contrato) return;

    setLoading(true);
    setError(null);

    getContractEquipment(contrato)
      .then(result => {
        if (result.success) {
          setEquipment(result.equipment || []);
        } else {
          setError(result.error || 'Erro ao carregar equipamentos');
        }
      })
      .catch(err => {
        console.error('Erro ao buscar equipamentos:', err);
        setError('Erro ao buscar equipamentos do contrato');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [contrato]);

  // Agrupar e ordenar equipamentos por Site > Modelo > Série (decrescente por quantidade)
  const groupedEquipment = useMemo(() => {
    const groups = {};

    equipment.forEach(item => {
      const siteKey = item.siteNome || 'Sem site';
      const modeloKey = item.modelo || 'Sem modelo';
      const serieKey = item.numSerie || item.patrimonio || 'Sem identificação';

      if (!groups[siteKey]) {
        groups[siteKey] = {};
      }
      if (!groups[siteKey][modeloKey]) {
        groups[siteKey][modeloKey] = [];
      }

      groups[siteKey][modeloKey].push({
        ...item,
        key: `${item.numSerie}-${item.patrimonio}-${item.codProduto}`
      });
    });

    // Calcular totais e ordenar
    const sitesWithTotals = Object.keys(groups).map(siteKey => {
      const modelos = Object.keys(groups[siteKey]);
      let totalEquipamentos = 0;
      
      modelos.forEach(modeloKey => {
        totalEquipamentos += groups[siteKey][modeloKey].length;
      });

      return {
        siteKey,
        totalEquipamentos,
        modelos: modelos.map(modeloKey => ({
          modeloKey,
          totalSeries: groups[siteKey][modeloKey].length,
          series: groups[siteKey][modeloKey]
        })).sort((a, b) => b.totalSeries - a.totalSeries)
      };
    });

    // Ordenar sites por quantidade decrescente
    return sitesWithTotals.sort((a, b) => b.totalEquipamentos - a.totalEquipamentos);
  }, [equipment]);

  // Filtrar equipamentos agrupados baseado no texto de busca
  const filteredGroupedEquipment = useMemo(() => {
    if (!searchText.trim()) {
      return groupedEquipment;
    }

    const searchLower = searchText.toLowerCase().trim();

    return groupedEquipment
      .map(site => {
        // Filtrar séries dentro dos modelos
        const filteredModelos = site.modelos
          .map(modelo => {
            const filteredSeries = modelo.series.filter(item => {
              const siteMatch = site.siteKey.toLowerCase().includes(searchLower);
              const modeloMatch = modelo.modeloKey.toLowerCase().includes(searchLower);
              const serieMatch = (item.numSerie || '').toLowerCase().includes(searchLower);
              const patrimonioMatch = (item.patrimonio || '').toLowerCase().includes(searchLower);
              
              return siteMatch || modeloMatch || serieMatch || patrimonioMatch;
            });

            if (filteredSeries.length === 0) return null;

            return {
              ...modelo,
              series: filteredSeries,
              totalSeries: filteredSeries.length
            };
          })
          .filter(Boolean);

        if (filteredModelos.length === 0) return null;

        const totalEquipamentos = filteredModelos.reduce((sum, modelo) => sum + modelo.totalSeries, 0);

        return {
          ...site,
          modelos: filteredModelos,
          totalEquipamentos
        };
      })
      .filter(Boolean);
  }, [groupedEquipment, searchText]);

  // Verificar se um item está selecionado
  const isItemSelected = (item) => {
    return selectedEquipments.some(sel => sel.key === item.key);
  };

  // Verificar se todos os itens de um modelo estão selecionados
  const isModeloFullySelected = (siteIndex, modeloIndex) => {
    const series = filteredGroupedEquipment[siteIndex]?.modelos[modeloIndex]?.series || [];
    return series.length > 0 && series.every(item => isItemSelected(item));
  };

  // Verificar se há seleção parcial em um modelo (alguns selecionados, mas não todos)
  const isModeloPartiallySelected = (siteIndex, modeloIndex) => {
    const series = filteredGroupedEquipment[siteIndex]?.modelos[modeloIndex]?.series || [];
    if (series.length === 0) return false;
    const selectedCount = series.filter(item => isItemSelected(item)).length;
    return selectedCount > 0 && selectedCount < series.length;
  };

  // Verificar se todos os itens de um site estão selecionados
  const isSiteFullySelected = (siteIndex) => {
    const modelos = filteredGroupedEquipment[siteIndex]?.modelos || [];
    return modelos.length > 0 && modelos.every((_, modeloIndex) => isModeloFullySelected(siteIndex, modeloIndex));
  };

  // Verificar se há seleção parcial em um site (alguns selecionados, mas não todos)
  const isSitePartiallySelected = (siteIndex) => {
    const modelos = filteredGroupedEquipment[siteIndex]?.modelos || [];
    if (modelos.length === 0) return false;
    
    let hasSelected = false;
    let hasUnselected = false;
    
    modelos.forEach((_, modeloIndex) => {
      const series = filteredGroupedEquipment[siteIndex]?.modelos[modeloIndex]?.series || [];
      const selectedCount = series.filter(item => isItemSelected(item)).length;
      if (selectedCount > 0) hasSelected = true;
      if (selectedCount < series.length) hasUnselected = true;
    });
    
    return hasSelected && hasUnselected;
  };

  // Toggle seleção de um item
  const toggleItemSelection = (item) => {
    const isSelected = isItemSelected(item);
    if (isSelected) {
      setSelectedEquipments(prev => prev.filter(sel => sel.key !== item.key));
    } else {
      setSelectedEquipments(prev => [...prev, item]);
    }
  };

  // Toggle seleção de um modelo
  const toggleModeloSelection = (siteIndex, modeloIndex) => {
    const series = filteredGroupedEquipment[siteIndex]?.modelos[modeloIndex]?.series || [];
    const isFullySelected = isModeloFullySelected(siteIndex, modeloIndex);

    if (isFullySelected) {
      setSelectedEquipments(prev => prev.filter(sel => 
        !series.some(item => item.key === sel.key)
      ));
    } else {
      const newItems = series.filter(item => !isItemSelected(item));
      setSelectedEquipments(prev => [...prev, ...newItems]);
    }
  };

  // Toggle seleção de um site
  const toggleSiteSelection = (siteIndex) => {
    const modelos = filteredGroupedEquipment[siteIndex]?.modelos || [];
    const isFullySelected = isSiteFullySelected(siteIndex);

    if (isFullySelected) {
      const allItems = modelos.flatMap(modelo => modelo.series);
      setSelectedEquipments(prev => prev.filter(sel => 
        !allItems.some(item => item.key === sel.key)
      ));
    } else {
      const allItems = modelos.flatMap(modelo => modelo.series);
      const newItems = allItems.filter(item => !isItemSelected(item));
      setSelectedEquipments(prev => [...prev, ...newItems]);
    }
  };

  // Toggle expansão de site
  const toggleSiteExpansion = (siteIndex) => {
    const newExpanded = new Set(expandedSites);
    if (newExpanded.has(siteIndex)) {
      newExpanded.delete(siteIndex);
      // Fechar modelos quando fechar site
      const modelos = filteredGroupedEquipment[siteIndex]?.modelos || [];
      modelos.forEach((_, modeloIndex) => {
        const key = `${siteIndex}-${modeloIndex}`;
        setExpandedModelos(prev => {
          const newModelos = new Set(prev);
          newModelos.delete(key);
          return newModelos;
        });
      });
    } else {
      newExpanded.add(siteIndex);
    }
    setExpandedSites(newExpanded);
  };

  // Toggle expansão de modelo
  const toggleModeloExpansion = (siteIndex, modeloIndex) => {
    const key = `${siteIndex}-${modeloIndex}`;
    setExpandedModelos(prev => {
      const newModelos = new Set(prev);
      if (newModelos.has(key)) {
        newModelos.delete(key);
      } else {
        newModelos.add(key);
      }
      return newModelos;
    });
  };

  // Notificar mudanças
  useEffect(() => {
    if (onEquipmentsSelected) {
      onEquipmentsSelected(selectedEquipments);
    }
  }, [selectedEquipments, onEquipmentsSelected]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOsTypeDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOsTypeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOsTypeDropdown]);

  // Handler para confirmar equipamentos e prosseguir
  const handleConfirmEquipments = (tipoOs) => {
    if (onConfirmEquipments && selectedEquipments.length > 0) {
      onConfirmEquipments(selectedEquipments, tipoOs);
    }
  };

  // Handler para verificar OS abertas
  const handleCheckOpenOS = async (tipoOs) => {
    if (selectedEquipments.length === 0) return;

    setIsCheckingOs(true);
    setShowOsTypeDropdown(false);
    setSelectedTipoOs(tipoOs);

    try {
      // Extrair séries dos equipamentos selecionados
      const series = selectedEquipments
        .map(eq => eq.numSerie)
        .filter(serie => serie && serie.trim() !== '');

      if (series.length === 0) {
        setIsCheckingOs(false);
        return;
      }

      const result = await checkOpenOSByType(series, tipoOs);

      if (result.success && result.osAbertas && result.osAbertas.length > 0) {
        setOsAbertas(result.osAbertas);
        setShowOsAbertasModal(true);
      } else {
        // Não há OS abertas, continuar com o fluxo normal
        handleConfirmEquipments(tipoOs);
      }
    } catch (error) {
      console.error('Erro ao verificar OS abertas:', error);
    } finally {
      setIsCheckingOs(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: 'var(--modal-bg, #ffffff)',
        borderRadius: '12px',
        padding: '40px 20px',
        border: '1px solid #e8e8e8',
        boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
        flex: '1 1 auto',
        minWidth: '520px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 250px)',
        maxHeight: 'calc(100vh - 250px)'
      }}>
        <div style={{
          width: '24px',
          height: '24px',
          border: '3px solid #7c4dff',
          borderTop: '3px solid transparent',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: 'var(--modal-bg, #ffffff)',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #e8e8e8',
        boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
        flex: '1 1 auto',
        minWidth: '520px',
        height: 'calc(100vh - 250px)',
        maxHeight: 'calc(100vh - 250px)'
      }}>
        <div style={{
          padding: '12px',
          background: 'var(--bg-surface-alt, #fff5f5)',
          border: '1px solid #feb2b2',
          borderRadius: '8px',
          fontSize: '12px',
          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
          color: '#c53030'
        }}>
          {error}
        </div>
      </div>
    );
  }

  const totalSelected = selectedEquipments.length;
  const totalAvailable = equipment.length;

  return (
    <div style={{
      background: 'var(--modal-bg, #ffffff)',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid #e8e8e8',
      boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
      flex: '1 1 auto',
      minWidth: '520px',
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 250px)',
      maxHeight: 'calc(100vh - 250px)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '16px',
        flexShrink: 0
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '14px',
          fontWeight: 600,
          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
          color: 'var(--text-body, #2d3748)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          flexShrink: 0
        }}>
          Selecionar Equipamentos
        </h3>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0
        }}>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Pesquisar informações..."
            style={{
              flex: '0 1 auto',
              minWidth: '200px',
              maxWidth: '300px',
              padding: '8px 12px',
              border: '2px solid var(--border-subtle, #e2e8f0)',
              borderRadius: '8px',
              fontSize: '13px',
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              color: 'var(--text-body, #2d3748)',
              outline: 'none',
              transition: 'all 0.2s ease',
              background: 'var(--modal-bg, #ffffff)'
            }}
            onFocus={(e) => e.target.style.borderColor = '#7c4dff'}
            onBlur={(e) => e.target.style.borderColor = 'var(--border-subtle, #e2e8f0)'}
          />
          {selectedEquipments.length > 0 && (
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setShowOsTypeDropdown(!showOsTypeDropdown)}
                disabled={isCheckingOs}
                style={{
                  padding: '8px 16px',
                  background: isCheckingOs ? '#a0aec0' : '#7c4dff',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 500,
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  cursor: isCheckingOs ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  if (!isCheckingOs) e.target.style.background = '#6d3fe8';
                }}
                onMouseLeave={(e) => {
                  if (!isCheckingOs) e.target.style.background = '#7c4dff';
                }}
              >
                {isCheckingOs ? (
                  <>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid var(--modal-bg, #ffffff)',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }}></div>
                    Verificando...
                  </>
                ) : (
                  'Tipo de OS'
                )}
              </button>
              
              {showOsTypeDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  background: 'var(--modal-bg, #ffffff)',
                  border: '1px solid var(--border-subtle, #e2e8f0)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  zIndex: 1000,
                  minWidth: '180px',
                  overflow: 'hidden'
                }}>
                  <button
                    type="button"
                    onClick={() => handleCheckOpenOS('N')}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--border-subtle, #f0f0f0)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)',
                      transition: 'background 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    className="open-os-list-item"
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-tools" style={{ fontSize: '11px' }}></i>
                    <span>Corretiva</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCheckOpenOS('S')}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--border-subtle, #f0f0f0)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)',
                      transition: 'background 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    className="open-os-list-item"
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-shield-check" style={{ fontSize: '11px' }}></i>
                    <span>Preventiva</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCheckOpenOS('A')}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--border-subtle, #f0f0f0)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)',
                      transition: 'background 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    className="open-os-list-item"
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-speedometer2" style={{ fontSize: '11px' }}></i>
                    <span>Aferição</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCheckOpenOS('D')}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--border-subtle, #f0f0f0)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)',
                      transition: 'background 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    className="open-os-list-item"
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-box-arrow-right" style={{ fontSize: '11px' }}></i>
                    <span>Desinstalação</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCheckOpenOS('R')}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--border-subtle, #f0f0f0)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)',
                      transition: 'background 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    className="open-os-list-item"
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-arrow-repeat" style={{ fontSize: '11px' }}></i>
                    <span>Recarga</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCheckOpenOS('B')}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)',
                      transition: 'background 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    className="open-os-list-item"
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <i className="bi bi-shop" style={{ fontSize: '11px' }}></i>
                    <span>Balcão</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{
        flex: '1 1 auto',
        overflowY: 'auto',
        overflowX: 'hidden',
        marginBottom: '12px',
        paddingRight: '8px',
        minHeight: 0
      }}>
        {filteredGroupedEquipment.length === 0 ? (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: 'var(--text-muted, #718096)',
            fontSize: '13px',
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif"
          }}>
            {searchText.trim() ? 'Nenhum resultado encontrado para a busca.' : 'Nenhum equipamento encontrado para este contrato.'}
          </div>
        ) : (
          <div style={{
            display: 'table',
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0
          }}>
            {filteredGroupedEquipment.map((site, siteIndex) => {
              const isSiteExpanded = expandedSites.has(siteIndex);
              const isSiteSelected = isSiteFullySelected(siteIndex);

              // Obter endereço do primeiro item do site para tooltip
              const firstItem = site.modelos?.[0]?.series?.[0];
              const enderecoTooltip = firstItem?.endereco ? [
                firstItem.endereco.logradouro,
                firstItem.endereco.numero,
                firstItem.endereco.bairro,
                firstItem.endereco.cidade
              ].filter(Boolean).join(', ') : '';

              return (
                <React.Fragment key={siteIndex}>
                  {/* Linha do Site */}
                  <div style={{
                    display: 'table-row',
                    background: (isSiteSelected || isSitePartiallySelected(siteIndex)) ? 'rgba(124, 77, 255, 0.12)' : 'var(--modal-bg, #ffffff)',
                    borderBottom: '1px solid var(--border-subtle, #e2e8f0)',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSiteSelected && !isSitePartiallySelected(siteIndex)) {
                      e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSiteSelected && !isSitePartiallySelected(siteIndex)) {
                      e.currentTarget.style.background = 'var(--modal-bg, #ffffff)';
                    }
                  }}
                  >
                    <div style={{
                      display: 'table-cell',
                      padding: '9px 16px',
                      verticalAlign: 'middle'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={isSiteSelected}
                            onChange={() => toggleSiteSelection(siteIndex)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: '13px',
                              height: '13px',
                              cursor: 'pointer',
                              accentColor: '#7c4dff',
                              flexShrink: 0,
                              opacity: isSitePartiallySelected(siteIndex) ? 0 : 1
                            }}
                          />
                          {isSitePartiallySelected(siteIndex) && (
                            <div
                              onClick={() => toggleSiteSelection(siteIndex)}
                              style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: '13px',
                                height: '13px',
                                background: '#7c4dff',
                                border: '2px solid #7c4dff',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <div style={{
                                width: '7px',
                                height: '2px',
                                background: 'var(--modal-bg, #ffffff)',
                                borderRadius: '1px'
                              }}></div>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => toggleSiteExpansion(siteIndex)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-muted, #718096)',
                            transition: 'transform 0.2s ease',
                            transform: isSiteExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            flexShrink: 0
                          }}
                        >
                          <i className="bi bi-chevron-right" style={{ fontSize: '12px' }}></i>
                        </button>
                        <div style={{
                          flex: 1,
                          minWidth: 0
                        }}>
                          <div 
                            style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              color: 'var(--text-body, #2d3748)'
                            }}
                            title={enderecoTooltip || undefined}
                          >
                            {site.siteKey}
                          </div>
                        </div>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: 'var(--text-muted, #718096)',
                          background: 'var(--bg-surface-alt, #f7fafc)',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          flexShrink: 0
                        }}>
                          {site.totalEquipamentos} {site.totalEquipamentos === 1 ? 'equipamento' : 'equipamentos'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Modelos do Site (quando expandido) */}
                  {isSiteExpanded && site.modelos.map((modelo, modeloIndex) => {
                    const modeloKey = `${siteIndex}-${modeloIndex}`;
                    const isModeloExpanded = expandedModelos.has(modeloKey);
                    const isModeloSelected = isModeloFullySelected(siteIndex, modeloIndex);

                    return (
                      <React.Fragment key={modeloKey}>
                        {/* Linha do Modelo */}
                        <div style={{
                          display: 'table-row',
                          background: (isModeloSelected || isModeloPartiallySelected(siteIndex, modeloIndex)) ? 'rgba(124, 77, 255, 0.08)' : 'var(--bg-surface-alt, #fafbfc)',
                          borderBottom: '1px solid var(--border-subtle, #f0f0f0)',
                          cursor: 'pointer',
                          transition: 'background 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!isModeloSelected && !isModeloPartiallySelected(siteIndex, modeloIndex)) {
                            e.currentTarget.style.background = '#f5f6fa';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isModeloSelected && !isModeloPartiallySelected(siteIndex, modeloIndex)) {
                            e.currentTarget.style.background = 'var(--bg-surface-alt, #fafbfc)';
                          }
                        }}
                        >
                          <div style={{
                            display: 'table-cell',
                            padding: '10px 16px 10px 56px',
                            verticalAlign: 'middle'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={isModeloSelected}
                                  onChange={() => toggleModeloSelection(siteIndex, modeloIndex)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: '14px',
                                    height: '14px',
                                    cursor: 'pointer',
                                    accentColor: '#7c4dff',
                                    flexShrink: 0,
                                    opacity: isModeloPartiallySelected(siteIndex, modeloIndex) ? 0 : 1
                                  }}
                                />
                                {isModeloPartiallySelected(siteIndex, modeloIndex) && (
                                  <div
                                    onClick={() => toggleModeloSelection(siteIndex, modeloIndex)}
                                    style={{
                                      position: 'absolute',
                                      left: 0,
                                      top: 0,
                                      width: '14px',
                                      height: '14px',
                                      background: '#7c4dff',
                                      border: '2px solid #7c4dff',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <div style={{
                                      width: '8px',
                                      height: '2px',
                                      background: 'var(--modal-bg, #ffffff)',
                                      borderRadius: '1px'
                                    }}></div>
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => toggleModeloExpansion(siteIndex, modeloIndex)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  padding: '4px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#a0aec0',
                                  transition: 'transform 0.2s ease',
                                  transform: isModeloExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                  flexShrink: 0
                                }}
                              >
                                <i className="bi bi-chevron-right" style={{ fontSize: '11px' }}></i>
                              </button>
                              <div style={{
                                flex: 1,
                                minWidth: 0
                              }}>
                                <div style={{
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                  color: 'var(--text-muted, #4a5568)'
                                }}>
                                  {modelo.modeloKey}
                                </div>
                              </div>
                              <div style={{
                                fontSize: '11px',
                                fontWeight: 500,
                                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                color: '#a0aec0',
                                background: 'var(--bg-surface-alt, #f7fafc)',
                                padding: '3px 6px',
                                borderRadius: '4px',
                                flexShrink: 0
                              }}>
                                {modelo.totalSeries} {modelo.totalSeries === 1 ? 'série' : 'séries'}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Séries do Modelo (quando expandido) */}
                        {isModeloExpanded && modelo.series.map((item) => {
                          const isSelected = isItemSelected(item);
                          return (
                            <div
                              key={item.key}
                              style={{
                                display: 'table-row',
                                background: isSelected ? 'rgba(124, 77, 255, 0.12)' : 'transparent',
                                borderBottom: '1px solid #f8f9fa',
                                cursor: 'pointer',
                                transition: 'background 0.2s ease'
                              }}
                              onClick={() => toggleItemSelection(item)}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.background = 'transparent';
                                }
                              }}
                            >
                              <div style={{
                                display: 'table-cell',
                                padding: '8px 16px 8px 96px',
                                verticalAlign: 'middle'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px'
                                }}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleItemSelection(item)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      width: '14px',
                                      height: '14px',
                                      cursor: 'pointer',
                                      accentColor: '#7c4dff',
                                      flexShrink: 0
                                    }}
                                  />
                                  <div style={{
                                    fontSize: '11px',
                                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                    color: 'var(--text-muted, #718096)',
                                    flex: 1
                                  }}>
                                    {item.numSerie && (
                                      <span style={{ marginRight: '12px' }}>
                                        Série: <strong style={{ color: 'var(--text-muted, #4a5568)' }}>{item.numSerie}</strong>
                                      </span>
                                    )}
                                    {item.patrimonio && (
                                      <span>
                                        Patrimônio: <strong style={{ color: 'var(--text-muted, #4a5568)' }}>{item.patrimonio}</strong>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de OS Abertas */}
      {showOsAbertasModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(4px)'
        }}
        onClick={(e) => {
          // Só fecha se clicar fora do conteúdo do modal
          if (e.target === e.currentTarget) {
            setShowOsAbertasModal(false);
          }
        }}
        >
          <div style={{
            background: 'var(--modal-bg, #ffffff)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '16px',
              fontWeight: 600,
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              color: 'var(--text-body, #2d3748)'
            }}>
              Ordens de Serviço Abertas Encontradas
            </h3>
            
            <div style={{
              marginBottom: '20px'
            }}>
              {osAbertas.map((item, index) => (
                <div key={index} style={{
                  padding: '12px',
                  background: 'var(--bg-surface-alt, #f7fafc)',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  border: '1px solid var(--border-subtle, #e2e8f0)'
                }}>
                  <div style={{
                    fontSize: '13px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-body, #2d3748)',
                    marginBottom: '4px'
                  }}>
                    <strong>Série:</strong> {item.serie}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-body, #2d3748)'
                  }}>
                    <strong>OS Aberta:</strong> {item.osCodigo}
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => {
                setShowOsAbertasModal(false);
                // Não chama handleConfirmEquipments, pois há OS abertas que impedem a continuação
              }}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: '#7c4dff',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#6d3fe8'}
              onMouseLeave={(e) => e.target.style.background = '#7c4dff'}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentSelectionStep;
